import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Types
export interface PricingPlan {
  plan_code: string;
  name: string;
  description: string;
  price: number | null;
  currency: string;
  billing_cycle: string;
  features: string[];
  is_popular: boolean;
  is_contact_only: boolean;
  badge_text?: string;
}

export interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export interface HomepageContent {
  section: string;
  title?: string;
  subtitle?: string;
  content?: string;
  metadata?: Record<string, unknown>;
  images?: Array<{ url: string; alt?: string }>;
}

// Default fallback data (for when API is unavailable)
const getDefaultPricing = (lang: string): PricingPlan[] => {
  const isKorean = lang === 'ko';
  const isIndonesian = lang === 'id';

  return [
    {
      plan_code: 'BASIC',
      name: isKorean ? 'Basic' : isIndonesian ? 'Dasar' : 'Basic',
      description: isKorean
        ? '소규모 팀을 위한 기본 플랜'
        : isIndonesian
        ? 'Paket dasar untuk tim kecil'
        : 'Basic plan for small teams',
      price: isIndonesian ? 75000 : isKorean ? 4350 : 5,
      currency: isIndonesian ? 'IDR' : isKorean ? 'KRW' : 'USD',
      billing_cycle: 'monthly',
      features: isKorean
        ? ['출퇴근 관리', '휴가 관리', '기본 보고서', '이메일 지원']
        : isIndonesian
        ? ['Manajemen Kehadiran', 'Manajemen Cuti', 'Laporan Dasar', 'Dukungan Email']
        : ['Attendance Management', 'Leave Management', 'Basic Reports', 'Email Support'],
      is_popular: false,
      is_contact_only: false,
    },
    {
      plan_code: 'PROFESSIONAL',
      name: isKorean ? 'Professional' : isIndonesian ? 'Profesional' : 'Professional',
      description: isKorean
        ? '성장하는 기업을 위한 전문 플랜'
        : isIndonesian
        ? 'Paket profesional untuk bisnis berkembang'
        : 'Professional plan for growing businesses',
      price: isIndonesian ? 150000 : isKorean ? 8700 : 10,
      currency: isIndonesian ? 'IDR' : isKorean ? 'KRW' : 'USD',
      billing_cycle: 'monthly',
      features: isKorean
        ? ['모든 Basic 기능', '전자결재', '조직도 관리', '대시보드', '모바일 앱', '우선 지원']
        : isIndonesian
        ? ['Semua Fitur Dasar', 'Persetujuan Elektronik', 'Bagan Organisasi', 'Dasbor', 'Aplikasi Seluler', 'Dukungan Prioritas']
        : ['All Basic Features', 'E-Approval', 'Organization Chart', 'Dashboard', 'Mobile App', 'Priority Support'],
      is_popular: true,
      is_contact_only: false,
      badge_text: 'Most Popular',
    },
    {
      plan_code: 'ENTERPRISE',
      name: isKorean ? 'Enterprise' : isIndonesian ? 'Perusahaan' : 'Enterprise',
      description: isKorean
        ? '대기업을 위한 맞춤형 솔루션'
        : isIndonesian
        ? 'Solusi khusus untuk perusahaan besar'
        : 'Custom solution for large enterprises',
      price: null,
      currency: isIndonesian ? 'IDR' : isKorean ? 'KRW' : 'USD',
      billing_cycle: 'monthly',
      features: isKorean
        ? ['모든 Professional 기능', 'AI 분석', 'API 연동', '전담 매니저', 'SLA 보장', '커스터마이징']
        : isIndonesian
        ? ['Semua Fitur Profesional', 'Analitik AI', 'Integrasi API', 'Manajer Khusus', 'Jaminan SLA', 'Kustomisasi']
        : ['All Professional Features', 'AI Analytics', 'API Integration', 'Dedicated Manager', 'SLA Guarantee', 'Customization'],
      is_popular: false,
      is_contact_only: true,
    },
  ];
};

// Currency formatter
export const formatPrice = (price: number | null, currency: string, lang: string): string => {
  if (price === null) {
    return lang === 'ko' ? '문의' : lang === 'id' ? 'Hubungi Kami' : 'Contact Us';
  }

  const locale = currency === 'IDR' ? 'id-ID' : currency === 'KRW' ? 'ko-KR' : 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(price);
};

// Hook: usePricing
export const usePricing = () => {
  const { i18n } = useTranslation();
  const [pricing, setPricing] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lang = i18n.language?.substring(0, 2) || 'id';
  const currency = lang === 'id' ? 'IDR' : lang === 'ko' ? 'KRW' : 'USD';

  useEffect(() => {
    const fetchPricing = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/homepage-config/public/pricing?lang=${lang}&currency=${currency}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch pricing');
        }

        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setPricing(data);
        } else {
          // Use default fallback if no data
          setPricing(getDefaultPricing(lang));
        }
      } catch (err) {
        console.warn('Using default pricing data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Use default fallback on error
        setPricing(getDefaultPricing(lang));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricing();
  }, [lang, currency]);

  return { pricing, isLoading, error, currency };
};

// Hook: useFAQ
export const useFAQ = () => {
  const { i18n } = useTranslation();
  const [faq, setFaq] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const lang = i18n.language?.substring(0, 2) || 'id';

  useEffect(() => {
    const fetchFAQ = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/homepage-config/public/faq?lang=${lang}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch FAQ');
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          setFaq(data);
        }
      } catch (err) {
        console.warn('FAQ fetch failed:', err);
        // FAQ is optional, so we just leave it empty
      } finally {
        setIsLoading(false);
      }
    };

    fetchFAQ();
  }, [lang]);

  return { faq, isLoading };
};

// Hook: useContent
export const useContent = (section: string) => {
  const { i18n } = useTranslation();
  const [content, setContent] = useState<HomepageContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const lang = i18n.language?.substring(0, 2) || 'id';

  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/homepage-config/public/content/${section}?lang=${lang}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch content');
        }

        const data = await response.json();
        setContent(data);
      } catch (err) {
        console.warn(`Content fetch failed for ${section}:`, err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [section, lang]);

  return { content, isLoading };
};
